/**
 * menu-drawer.js
 */

import React from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {
  TransitionGroup,
  CSSTransition
} from "react-transition-group";
import { useLocation, matchPath, Switch } from 'react-router-dom';
import { useHookstate } from '@hookstate/core';
import { shouldRenderMobileLayout } from 'utils/app-context';
import Route from 'routing/route';
import DesktopDrawer from 'components/menu-drawer/desktop';
import MobileDrawer from 'components/menu-drawer/mobile';


const useStyles = makeStyles(theme => ({
  root: {
    '& > div': {
      height: '100vh',
      width: '100vw',
      zIndex: 1300,
      position: 'fixed',
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      display: 'flex',
    }
  },
  closer: {
    flex: 1
  },
  drawerContainer: {
    boxShadow: theme.shadows[15],
    height: '100%',
    width: theme.size.menuDrawer.width,
    minWidth: theme.size.menuDrawer.minWidth,
    maxWidth: theme.size.menuDrawer.maxWidth,
    [theme.breakpoints.down(theme.size.menuDrawer.minWidth)]: {
      minWidth: '100%',
      maxWidth: '100%',
      width: 'auto',
    }
  },
  drawerEnter: {
    backgroundColor: 'rgba(0,0,0,0)',
    '& > div': {
      transform: 'translateX(100%)',
      [theme.breakpoints.down('sm_md')]: {
        transform: 'translateY(100%)',
      }
    },
  },
  drawerEnterActive: {
    transition: `${theme.transitions.duration.drawer}ms ${theme.transitions.easing.easeOut}`,
    transitionDelay: 50,
    backgroundColor: 'rgba(0,0,0,0.6)',
    // faster mobile animation
    [theme.breakpoints.down('sm')]: {
      transition: `${theme.transitions.duration.page}ms ${theme.transitions.easing.easeInOut}`,
    },
    '& > div': {
      transition: `${theme.transitions.duration.drawer}ms ${theme.transitions.easing.easeOut}`,
      transitionDelay: 50,
      transform: 'translateX(0%) translateY(0%) !important',
      // faster mobile animation
      [theme.breakpoints.down('sm')]: {
        transition: `${theme.transitions.duration.page}ms ${theme.transitions.easing.easeInOut}`,
      }
    },
  },
  drawerEnterDone: {
    backgroundColor: 'rgba(0,0,0,0.6)'
  },
  drawerExit: {
    backgroundColor: 'rgba(0,0,0,0.6)',
    '& > div': {
      transform: 'translateX(0%) translateY(0%)',
    }
  },
  drawerExitActive: {
    transition: `${theme.transitions.duration.drawer}ms ${theme.transitions.easing.easeIn}`,
    transitionDelay: 100,
    backgroundColor: 'rgba(0,0,0,0)',
    // faster mobile animation
    [theme.breakpoints.down('sm')]: {
      transition: `${theme.transitions.duration.page}ms ${theme.transitions.easing.easeInOut}`,
    },
    '& > div': {
      transition: `${theme.transitions.duration.drawer}ms ${theme.transitions.easing.easeIn}`,
      transitionDelay: 100,
      transform: 'translateX(100%)',
      [theme.breakpoints.down('sm_md')]: {
        transform: 'translateY(100%)',
      },
      // faster mobile animation
      [theme.breakpoints.down('sm')]: {
        transition: `${theme.transitions.duration.page}ms ${theme.transitions.easing.easeInOut}`,
      }
    }
  },
}));


export default function MenuDrawer(props) {
  const {
    drawerPath,
    basePath,
    menuRoutes,
    desktopRoutes,
    mobileRoutes,
    onClose,
    wizard,
    title,
    subtitle,
    recordGroupType,
  } = props;
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });
  let location = useLocation();
  const drawerBasePath = basePath + drawerPath;
  const match = matchPath(location.pathname, drawerBasePath);
  const isDrawerRoute = Boolean(match);
  const drawerState = useHookstate({
    sidebarText: {
      text: {
        title: '',
        info: ''
      },
      isFocused: false
    },
    loading: {
      menu: false,
      content: false
    }
  });


  // reset sidebar text when route change
  React.useEffect(() => {
    drawerState.sidebarText.set({
      text: {
        title: '',
        info: ''
      },
      isFocused: false
    });

  // drawerState is hookstate setter
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);


  // reset loading when drawer closes
  React.useEffect(() => {
    if (!isDrawerRoute) {
      drawerState.loading.set({
        menu: false,
        content: false
      });
    }

  // drawerState is hookstate setter
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawerRoute]);

  return (
    <>
      <TransitionGroup className={`${classes.root} ${props.className}`}>
        <CSSTransition
          key={match ? match.path : ''}
          timeout={isMobile
            ? theme.transitions.duration.page + 100
            : theme.transitions.duration.drawer + 300} // the extra time lets the animation finish before removing DOM items
          in={true}
          appear={true}
          classNames={{
            appearDone: classes.drawerEnterDone,
            enter: classes.drawerEnter,
            enterActive: classes.drawerEnterActive,
            enterDone: classes.drawerEnterDone,
            exit: classes.drawerExit,
            exitActive: classes.drawerExitActive
          }}
        >
          <Switch location={location}>
            <Route path={drawerBasePath}>
              <div>
                <div onClick={onClose} className={classes.closer}></div>
                <div className={classes.drawerContainer}>

                  {/* mobile drawer */}
                  {shouldRenderMobileLayout() &&
                  <MobileDrawer
                    {...props}
                    routes={mobileRoutes}
                    wizard={wizard}
                    basePath={basePath}
                    onClose={onClose}
                    drawerState={drawerState}
                    recordGroupType={recordGroupType}
                  />}

                  {/* desktop drawer */}
                  {!shouldRenderMobileLayout() &&
                  <DesktopDrawer
                    {...props}
                    icon="training"
                    title={title}
                    subtitle={subtitle}
                    routes={desktopRoutes}
                    menuRoutes={menuRoutes}
                    wizard={wizard}
                    basePath={basePath}
                    onClose={onClose}
                    drawerState={drawerState}
                    recordGroupType={recordGroupType}
                  />}

                </div>
              </div>
            </Route>
          </Switch>
        </CSSTransition>
      </TransitionGroup>
    </>
  );
}